






























import Enumerable from "linq";
import InfiniteLoading from "vue-infinite-loading";
import { ElTree } from "element-ui/types/tree";
import { Component, Mixins, Prop, Watch, Emit } from "vue-property-decorator";
import {
  EditableFormatSummary,
  IEditableFormatSummaryTreeNode,
  IEditableFormatSummaryTreeData
} from "../../../../domain/editableFormat";
import { Directory } from "../../../../../base/domain/drawing";
import { TimeMeasure } from "../../../../../base/utilities";
import { ModelEditableMixin } from "../../../../../base/components/system/mixins";
import { DirectoryTreeNodesFactory } from "../../../../../base/components/system/utilities";
import { IFormatTreeSelectable } from ".";
import { ICancelSelectFormatTree } from "../../../../../documaker/components/format/selectors/trees";
import { FormatTreeNodeUtility } from "./utilities";

@Component({
  components: {
    InfiniteLoading
  },
})
export default class EditableFormatSummariesTreeSelector extends Mixins(ModelEditableMixin) implements IFormatTreeSelectable, ICancelSelectFormatTree {
  private static readonly _nodeUtility = new FormatTreeNodeUtility();

  chunkedCount: number = 0;
  loadedNodes: IEditableFormatSummaryTreeData[] = [];
  nodesData: IEditableFormatSummaryTreeData[] = [];
  selectedNode: IEditableFormatSummaryTreeNode = null;
  updateLoadChunkSize: number = 0;
  lastSelectedNodekey: number = null;

  $refs!: {
    tree: ElTree<number, IEditableFormatSummaryTreeData>,
    infiniteLoading: InfiniteLoading
  };

  @Prop({ default: 0 })
  loadChunkSize: number;

  @Prop({ default: () => [] as Directory<number, EditableFormatSummary>[] })
  directories: Directory<number, EditableFormatSummary>[];

  @Prop()
  filter: (source: EditableFormatSummary | Directory<number, EditableFormatSummary>) => boolean;

  @Prop({ default: true })
  selectedDirectoryOnly: boolean;

  @Prop({ default: false })
  displayAllNode: boolean;

  @Prop({ default: false })
  shouldShowEmptyFolder: boolean;

  created(): void {
    this.updateEditingSource();
  }

  get shouldInfiniteLoading(): boolean {
    return this.loadChunkSize > 0;
  }

  get usedTreeData(): IEditableFormatSummaryTreeData[] {
    return this.shouldInfiniteLoading
      ? this.loadedNodes
      : this.filteredNodesData;
  }

  get usedLoadChunkSize(): number {
    return Math.max(this.updateLoadChunkSize, this.loadChunkSize);
  }

  onInfinite(): void {
    this.chunkList();
  }

  private chunkList(): void {
    for (let i = this.chunkedCount; i < (this.chunkedCount + this.usedLoadChunkSize); i++) {
      if (i >= this.filteredNodesData.length) {
        this.initialSelection();
        this.$refs.infiniteLoading.stateChanger.complete();
        return;
      }
      this.loadedNodes.push(this.filteredNodesData[i]);
    }
    this.chunkedCount += this.usedLoadChunkSize;
    this.initialSelection();
    this.$refs.infiniteLoading.stateChanger.loaded();
  }

  private resetChunkList(): void {
    this.loadedNodes = [];
    this.chunkedCount = 0;
    if (this.$refs.infiniteLoading) {
      this.$refs.infiniteLoading.stateChanger.reset();
    }
  }

  private initialSelection(): void {
    this.updateLoadChunkSize = 0;
    if (this.directories.length > 0 && this.lastSelectedNodekey !== null) {
      this.$nextTick(() => {
        this.selectDirectoryNode(this.lastSelectedNodekey);
        this.lastSelectedNodekey = null;
      });
    }
  }

  selectDirectoryNode(key: number): boolean {
    if (!this.$refs.tree) {
      return false;
    }
    const found = this.findDirectoryNode(key);
    if (!found) {
      return false;
    }
    EditableFormatSummariesTreeSelector._nodeUtility.expandNode((this.$refs.tree as any).root, key);
    this.$nextTick(() => {
      this.$refs.tree.setCurrentKey(found.key);
      this.selectedNode = found;
    });
    return true;
  }

  private findDirectoryNode(key: number, node?: IEditableFormatSummaryTreeNode): IEditableFormatSummaryTreeNode {
    return EditableFormatSummariesTreeSelector._nodeUtility.findNode(
      node ? node : (this.$refs.tree as any).root,  // rootが必要なのでanyに変換
      n => ((n.data.source instanceof Directory) && (n.data.source.key === key))
    );
  }

  @Watch("filter")
  onFilterUpdated(
    newValue: (source: EditableFormatSummary | Directory<number, EditableFormatSummary>) => boolean,
    oldValue: (source: EditableFormatSummary | Directory<number, EditableFormatSummary>) => boolean
  ): void {
    this.refresh();
  }

  @Watch("shouldShowEmptyFolder")
  onShouldShowEmptyFolderUpdated(): void {
    this.refresh();
  }

  private refresh() {
    this.createNodes();
    this.resetChunkList();
    this.cancelSelectFormatTree();
    this.updateEditingSource();
  }

  private filterNode(filter: (source: EditableFormatSummary | Directory<number, EditableFormatSummary>) => boolean, data: IEditableFormatSummaryTreeData): boolean {
    // TODO: 途中のフォルダも見たほうがよい？
    data.children = Enumerable.from(data.children)
      .where(c => this.filterNode(this.filter, c))
      .toArray();
    const entities = data.source instanceof Directory ? data.source.entities : [];
    return this.displayAllNode
      || this.shouldShowEmptyFolder
      || filter(data.source) && (entities instanceof Array) && (0 < entities.length) && Enumerable.from(entities).any(e => filter(e))
      || (0 < data.children.length);
  }

  private createNodes(): void {
    TimeMeasure.log(
      "TreeNodes生成",
      () => {
        const factory = new DirectoryTreeNodesFactory<number, EditableFormatSummary>();
        this.nodesData = factory.create(this.directories);
      },
      "",
      TimeMeasure.debug
    );
  }

  get filteredNodesData(): IEditableFormatSummaryTreeData[] {
    return Enumerable.from(this.nodesData)
      .where(d => this.filterNode(this.filter, d))
      .toArray();
  }

  expandTree(): void {
    EditableFormatSummariesTreeSelector._nodeUtility.expandNode((this.$refs.tree as any).root);  // rootが必要なのでanyに変換
  }

  requestTreeNodeSelectStatus(): void {
    this.treeNodeSelectUpdated(this.selectedNode !== null);
  }

  cancelSelectFormatTree(): void {
    this.$refs.tree.setCurrentKey(null);
    this.selectedNode = null;
    this.treeNodeSelectUpdated(false);
  }

  updateNodeExpand(node: IEditableFormatSummaryTreeData, nodeProperty: IEditableFormatSummaryTreeNode, nodeSelf: any): void {
    EditableFormatSummariesTreeSelector._nodeUtility.updateNodeExpanded(node, nodeProperty, nodeSelf);
  }

  handleNodeClicked(node: IEditableFormatSummaryTreeData, nodeProperty: IEditableFormatSummaryTreeNode, nodeSelf: any): void {
    this.selectedNode = nodeProperty;
  }

  @Watch("selectedNode")
  onSelectedNodeUpdated(newValue: IEditableFormatSummaryTreeNode, oldValue: IEditableFormatSummaryTreeNode) {
    this.treeNodeSelectUpdated(newValue !== null);
    this.treeNodeSelected(newValue);
    this.updateEditingSource();
  }

  @Watch("directories")
  onDirectoriesUpdated(newValue: Directory<number, EditableFormatSummary>[], oldValue: Directory<number, EditableFormatSummary>[]) {
    if (newValue.length === 0) {  // ツリーの再読み込みが行われるタイミング
      this.nextInitialSelectionSetting();
    }
    this.createNodes();
    this.resetChunkList();
    if (this.$refs.tree) {
      this.cancelSelectFormatTree();
    }
    this.updateEditingSource();
  }

  private nextInitialSelectionSetting(): void {
    this.updateLoadChunkSize = this.usedTreeData.length;
    this.lastSelectedNodekey = this.selectedNode !== null && this.selectedNode.data.source instanceof Directory
      ? this.selectedNode.data.source.key
      : null;
  }

  private updateEditingSource(): void {
    // ツリー選択を選択ノードのフォーマットだけを表示するのでなくそのノード以下のフォーマットを表示するよう変更.(未選択時は全て)
    // フィルタっぽい動作なのでフィルタとして実装した方がよい気がするが、今から対応するのは当りがデカいのでフォーマットの配列を返す実装を維持.
    TimeMeasure.log(
      "選択肢更新",
      () => {
        const selectedDirectory = (this.selectedNode && this.selectedNode.data)
          ? this.selectedNode.data.source
          : null;
        const entities = this.selectedDirectoryOnly
          ? (selectedDirectory && (selectedDirectory instanceof Directory))
            ? this.extractSelectedDirectoryEntities(selectedDirectory)
            : []
          : (selectedDirectory && (selectedDirectory instanceof Directory))
            ? this.extractDirectoryEntities(selectedDirectory)
            : this.extractDirectoriesEntities(this.directories);
        this.editingSource = this.distinctEntities(entities);
      },
      "",
      TimeMeasure.debug
    );
  }

  private extractSelectedDirectoryEntities(source: Directory<number, EditableFormatSummary>): EditableFormatSummary[] {
    return Enumerable.from(!this.filter || this.filter(source) ? source.entities : [])
      .where(e => !this.filter || this.filter(e))
      .toArray();
  }

  private extractDirectoryEntities(source: Directory<number, EditableFormatSummary>): EditableFormatSummary[] {
    return Enumerable.from(!this.filter || this.filter(source) ? source.entities : [])
      .where(e => !this.filter || this.filter(e))
      .concat(this.extractDirectoriesEntities(source.nodes))
      .toArray();
  }

  private extractDirectoriesEntities(source: Directory<number, EditableFormatSummary>[]): EditableFormatSummary[] {
    return Enumerable.from(source)
      .where(d => !this.filter || this.filter(d))
      .select(d => this.extractDirectoryEntities(d))
      .aggregate([], (s, e) => s.concat(e));
  }

  private distinctEntities(source: EditableFormatSummary[]): EditableFormatSummary[] {
    return Enumerable.from(source)
      .groupBy(e => JSON.stringify({ id: e.id, name: e.name }),
        e => e,
        (k, g) => g.first())
      .toArray();
  }

  @Emit("treeNodeSelectUpdated")
  treeNodeSelectUpdated(value: boolean): void {}

  @Emit("nodeDoubleClicked")
  nodeDoubleClicked(): void {}

  @Emit("treeNodeSelected")
  treeNodeSelected(value: IEditableFormatSummaryTreeNode): void {}

  handleNodeDoubleClicked(event: PointerEvent): void {
    if (this.selectedNode.data.source instanceof EditableFormatSummary) {
      this.nodeDoubleClicked();
    }
    if (this.selectedNode.data.source instanceof Directory) {
      // 開閉反転
      // el-treeのexpand-on-click-nodeをfalseにしないと開閉が激しい・・
      // this.selectedNode.expanded = !this.selectedNode.expanded;
    }
  }
}
